import { Trash2 } from 'lucide-react';

import {
  CATEGORY_STYLES,
  CONFIDENCE_STYLES,
  DEFAULT_CATEGORY_STYLE,
  LOCATED_BY_LABELS,
  SOURCE_LABELS,
} from '../../constants/redaction';
import type {
  CandidateReport,
  ManualRedaction,
  RedactionCandidate,
  RedactionCategory,
  RedactionCategoryInfo,
} from '../../types/redaction';

export default function CandidateList({
  report,
  availableCategories,
  selectedIds,
  grounds,
  manualItems,
  onToggle,
  onGroundChange,
  onFocusCandidate,
  onManualChange,
  onManualRemove,
  onFocusManual,
}: {
  report: CandidateReport;
  availableCategories: RedactionCategoryInfo[];
  selectedIds: Set<string>;
  grounds: Record<string, string>;
  manualItems: ManualRedaction[];
  onToggle: (id: string) => void;
  onGroundChange: (id: string, value: string) => void;
  onFocusCandidate: (candidate: RedactionCandidate) => void;
  onManualChange: (id: string, patch: Partial<ManualRedaction>) => void;
  onManualRemove: (id: string) => void;
  onFocusManual: (item: ManualRedaction) => void;
}) {
  const empty = report.candidates.length === 0 && manualItems.length === 0;
  const categoryLabels = new Map(
    availableCategories.map((category) => [category.value, category.label]),
  );

  return (
    <div className="divide-y divide-gray-100">
      {report.text_truncated && (
        <div className="bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          This document is long and only the first part was analysed
          automatically. Check the later pages yourself.
        </div>
      )}
      {report.ocr_incomplete && (
        <div className="bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          Not every image page could be read automatically. Draw boxes on
          those pages over anything that should be withheld.
        </div>
      )}

      {manualItems.map((item, index) => (
        <div key={item.id} className="bg-emerald-50/40 px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <button
                type="button"
                onClick={() => onFocusManual(item)}
                className="block w-full text-left text-sm font-semibold text-gray-900 hover:text-emerald-700"
              >
                Drawn on page {item.box.page + 1}
                {manualItems.length > 1 ? ` · box ${index + 1}` : ''}
              </button>
              <p className="mt-0.5 text-xs text-gray-500">
                Added by you. Choose a category and a withholding ground.
              </p>
              <select
                value={item.category}
                onChange={(event) =>
                  onManualChange(item.id, {
                    category: event.target.value as RedactionCategory | '',
                    ground:
                      event.target.value &&
                      report.default_grounds[
                        event.target.value as RedactionCategory
                      ]
                        ? report.default_grounds[
                            event.target.value as RedactionCategory
                          ] ?? item.ground
                        : item.ground,
                  })
                }
                className={`mt-2 w-full rounded-lg border px-2 py-1.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${
                  item.category
                    ? 'border-gray-200 text-gray-700'
                    : 'border-amber-300 text-amber-800'
                }`}
                aria-label="Category for drawn box"
              >
                <option value="">Choose a category...</option>
                {availableCategories.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              <select
                value={item.ground}
                onChange={(event) =>
                  onManualChange(item.id, { ground: event.target.value })
                }
                className={`mt-2 w-full rounded-lg border px-2 py-1.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${
                  item.ground
                    ? 'border-gray-200 text-gray-700'
                    : 'border-amber-300 text-amber-800'
                }`}
                aria-label="Withholding ground for drawn box"
              >
                <option value="">Choose a withholding ground...</option>
                {report.withholding_grounds.map((option) => (
                  <option key={option.value} value={option.value}>
                    {`s${option.value} — ${option.label}`}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => onManualRemove(item.id)}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-white hover:text-red-600"
              aria-label="Remove drawn box"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}

      {empty && (
        <div className="px-6 py-8 text-center text-sm text-gray-500">
          Nothing was found in the categories you chose. Draw a box on the
          page over anything that should be withheld, or go back and add your
          own terms.
        </div>
      )}

      {report.candidates.map((candidate) => {
        const checked = selectedIds.has(candidate.id);
        const ground = grounds[candidate.id] ?? '';
        return (
          <div
            key={candidate.id}
            className={`px-4 py-3 transition ${checked ? 'bg-white' : 'bg-gray-50/70'}`}
          >
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(candidate.id)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                aria-label={`Withhold ${candidate.text}`}
              />
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => onFocusCandidate(candidate)}
                  className="block w-full truncate text-left text-sm font-semibold text-gray-900 hover:text-emerald-700"
                  title={candidate.text}
                >
                  {candidate.text}
                </button>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  <span
                    className={`rounded-full px-2 py-0.5 font-semibold ${CATEGORY_STYLES[candidate.category] ?? DEFAULT_CATEGORY_STYLE}`}
                  >
                    {categoryLabels.get(candidate.category) ?? candidate.category}
                  </span>
                  <span className="text-gray-400">
                    {SOURCE_LABELS[candidate.source] ?? candidate.source}
                  </span>
                  {candidate.located_by && candidate.located_by !== 'text_layer' && (
                    <span className="text-gray-400">
                      {LOCATED_BY_LABELS[candidate.located_by]}
                    </span>
                  )}
                  {candidate.confidence && (
                    <span
                      className={CONFIDENCE_STYLES[candidate.confidence] ?? 'text-gray-500'}
                    >
                      {candidate.confidence} confidence
                    </span>
                  )}
                  <span className="text-gray-500 tabular-nums">
                    {candidate.hit_count === 1
                      ? '1 place'
                      : `${candidate.hit_count} places`}
                  </span>
                </div>

                {candidate.hit_count === 0 && checked && (
                  <p className="mt-1.5 rounded-lg bg-amber-50 px-2 py-1 text-xs text-amber-800">
                    Not found in the page text or in images. It will still be
                    removed from document properties, form fields and
                    attachments, but draw a box if it appears as a picture.
                  </p>
                )}

                {candidate.reason && (
                  <p className="mt-1 text-xs text-gray-500">{candidate.reason}</p>
                )}

                {candidate.also_covers && candidate.also_covers.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Also withholds:{' '}
                    <span className="font-medium text-gray-700">
                      {candidate.also_covers.join(', ')}
                    </span>
                  </p>
                )}

                {checked && (
                  <select
                    value={ground}
                    onChange={(event) =>
                      onGroundChange(candidate.id, event.target.value)
                    }
                    className={`mt-2 w-full rounded-lg border px-2 py-1.5 text-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 ${
                      ground ? 'border-gray-200 text-gray-700' : 'border-amber-300 text-amber-800'
                    }`}
                    aria-label={`Withholding ground for ${candidate.text}`}
                  >
                    <option value="">Choose a withholding ground...</option>
                    {report.withholding_grounds.map((option) => (
                      <option key={option.value} value={option.value}>
                        {`s${option.value} — ${option.label}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
